import type { ReviewSourceId } from '@flexbyt/shared';
import { SourceGlyph } from '@flexbyt/widget-preview';
import { track } from '../../lib/analytics.js';
import { ButtonLink } from '../ui/button.js';
import { Heading, Text } from '../ui/type.js';

/**
 * The agency teaser.
 *
 * ===========================================================================
 * A different shape from everything above it
 * ===========================================================================
 * Three client sites, each with its own reviews scattered across its own
 * platforms, all handled the same way. That is a workflow picture, and it is
 * the one thing on this page that is not about a single business's own reviews
 * — which is exactly why it gets its own visual language rather than another
 * list of source rows.
 *
 * ===========================================================================
 * What it deliberately does not claim
 * ===========================================================================
 * No white-labelling, no client portals, no team seats, no agency dashboard.
 * The product has none of those: `User.workspaces` is an array, so one login
 * *can* belong to several workspaces, but there is no customer-facing switcher
 * and no API route to change workspace — only the staff backoffice sees more
 * than one.
 *
 * So the copy says the true thing, which is still worth saying: reviews are a
 * job on every client site, and this is one way of doing it that works the same
 * every time. The page it links to is where any real agency features get
 * described, once they exist.
 */

const CLIENTS: { name: string; kind: string; sources: ReviewSourceId[] }[] = [
  { name: 'Ashcombe House', kind: 'Boutique hotel', sources: ['tripadvisor', 'google'] },
  { name: 'Marlow & Fitch', kind: 'Law firm', sources: ['google'] },
  { name: 'Northgate Cycles', kind: 'Bike shop', sources: ['google', 'trustpilot', 'yelp'] },
];

export function AgenciesTeaser() {
  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-20">
      <div>
        <p className="eyebrow text-brand">For agencies and freelancers</p>
        <Heading level={2} className="mt-4">
          Building sites for other people?
        </Heading>
        <Text className="mt-4 max-w-prose">
          Reviews are one of the last things to go on a client site and one of
          the first things the client asks about. ReviewsJet gives you the same
          answer every time, whatever platform their reviews happen to be on.
        </Text>

        <div className="mt-8">
          <ButtonLink
            to="/agencies"
            variant="secondary"
            onClick={() => track('agency_cta_clicked', { surface: 'homepage' })}
          >
            ReviewsJet for agencies
          </ButtonLink>
        </div>
      </div>

      {/*
        One agency workspace rather than three loose rows: the point is that the
        clients live side by side in a single account.
      */}
      <div className="relative isolate">
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(closest-side,rgba(109,60,245,0.12),transparent)]"
        />
        <div className="rounded-[2rem] bg-white/60 p-2 shadow-[0_40px_120px_-40px_rgba(20,40,120,0.35)] ring-1 ring-black/[0.06] backdrop-blur">
          <div className="overflow-hidden rounded-[1.5rem] bg-white ring-1 ring-black/[0.07]">
            <div className="flex items-center justify-between border-b border-black/[0.06] bg-surface/70 px-5 py-3.5">
              <p className="text-body-sm font-semibold text-ink-950">Client sites</p>
              <p className="nums text-caption text-ink-500">
                {CLIENTS.length} workspaces
              </p>
            </div>

            <ul
              className="divide-y divide-black/[0.06]"
              aria-label="Three example client sites and the platforms their reviews come from"
            >
              {CLIENTS.map((client) => (
                <li key={client.name} className="flex items-center gap-4 px-5 py-4">
                  <span
                    aria-hidden
                    className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-wash text-body-sm font-bold text-brand"
                  >
                    {client.name.charAt(0)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body-sm font-bold text-ink-950">
                      {client.name}
                    </p>
                    <p className="mt-0.5 text-caption text-ink-500">{client.kind}</p>
                  </div>

                  <ul className="flex shrink-0 items-center -space-x-1.5">
                    {client.sources.map((source) => (
                      <li
                        key={source}
                        className="grid size-8 place-items-center rounded-full bg-white ring-2 ring-white shadow-[0_0_0_1px_rgba(10,10,11,0.08)]"
                      >
                        <SourceGlyph source={source} className="size-4" />
                      </li>
                    ))}
                  </ul>

                  <span className="hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-caption font-semibold text-emerald-700 sm:inline-flex">
                    <span className="size-1.5 rounded-full bg-emerald-500" />
                    Live
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
